import { useState, useMemo } from 'react';

type ProjectType = 'deck' | 'fence';

// ─── Deck config ───────────────────────────────────────────
const DECK_MATERIALS = [
  { id: 'pt', label: 'Pressure Treated', rate: 22, note: 'Budget-friendly & durable' },
  { id: 'cedar', label: 'Cedar', rate: 32, note: 'Natural look, great longevity' },
  { id: 'composite', label: 'Composite', rate: 45, note: 'Low maintenance, premium look' },
  { id: 'pvc', label: 'PVC / Vinyl', rate: 55, note: 'Highest durability, no rot' },
];
const FENCE_MATERIALS = [
  { id: 'pt', label: 'Pressure Treated', rate: 55, note: 'Standard & economical' },
  { id: 'cedar', label: 'Cedar', rate: 42, note: 'Natural beauty & strength' },
  { id: 'vinyl', label: 'Vinyl / PVC', rate: 48, note: 'Low maintenance, clean finish' },
  { id: 'aluminum', label: 'Aluminum', rate: 58, note: 'Rust-free, modern style' },
];
const FENCE_HEIGHTS = [
  { id: '4', label: '4 ft', multiplier: 0.85 },
  { id: '6', label: '6 ft', multiplier: 1.0 },
  { id: '8', label: '8 ft', multiplier: 1.4 },
];
const FENCE_STYLES = [
  {
    id: 'traditional',
    label: 'Traditional',
    multiplier: 1.0,
    ptRate: 55,
    note: 'Classic picket or board-on-board spacing',
    icon: 'ri-grid-line',
  },
  {
    id: 'semi-privacy',
    label: 'Semi Privacy',
    multiplier: 1.18,
    ptRate: 60,
    note: 'Partial sight-lines, good airflow',
    icon: 'ri-layout-column-line',
  },
  {
    id: 'full-privacy',
    label: 'Full Privacy',
    multiplier: 1.38,
    ptRate: 65,
    note: 'Solid panels, maximum seclusion',
    icon: 'ri-layout-fill',
  },
];

const WOOD_MATERIALS = ['pt', 'cedar'];

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);
}

function InputNumber({
  label,
  value,
  onChange,
  min,
  max,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  unit: string;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</label>
      <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-10 h-11 flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-600 text-lg font-bold transition-colors cursor-pointer"
        >
          −
        </button>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={(e) => {
            const v = parseInt(e.target.value) || min;
            onChange(Math.min(max, Math.max(min, v)));
          }}
          className="flex-1 text-center text-sm font-semibold text-gray-800 py-2.5 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + 1))}
          className="w-10 h-11 flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-600 text-lg font-bold transition-colors cursor-pointer"
        >
          +
        </button>
        <span className="pr-3 text-xs text-gray-400 font-medium">{unit}</span>
      </div>
    </div>
  );
}

function MaterialCard({
  item,
  selected,
  onSelect,
}: {
  item: { id: string; label: string; rate: number; note: string };
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3.5 rounded-lg border-2 transition-all cursor-pointer ${
        selected
          ? 'border-[#2B5741] bg-[#2B5741]/5'
          : 'border-gray-200 hover:border-[#2B5741]/40'
      }`}
    >
      <p className={`text-sm font-semibold ${selected ? 'text-[#2B5741]' : 'text-gray-800'}`}>{item.label}</p>
      <p className="text-[11px] text-gray-400 mt-0.5">{item.note}</p>
    </button>
  );
}

function StyleCard({
  item,
  selected,
  onSelect,
}: {
  item: { id: string; label: string; multiplier: number; note: string; icon: string };
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3.5 rounded-lg border-2 transition-all cursor-pointer flex items-start gap-3 ${
        selected
          ? 'border-[#2B5741] bg-[#2B5741]/5'
          : 'border-gray-200 hover:border-[#2B5741]/40'
      }`}
    >
      <div className={`w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0 mt-0.5 ${
        selected ? 'bg-[#2B5741] text-white' : 'bg-gray-100 text-gray-500'
      }`}>
        <i className={`${item.icon} text-sm`}></i>
      </div>
      <div>
        <p className={`text-sm font-semibold ${selected ? 'text-[#2B5741]' : 'text-gray-800'}`}>{item.label}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{item.note}</p>
      </div>
    </button>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <label className="flex items-center justify-between cursor-pointer group">
      <span className="text-sm text-gray-700 font-medium group-hover:text-[#2B5741] transition-colors">{label}</span>
      <button
        type="button"
        onClick={onChange}
        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 cursor-pointer ${
          checked ? 'bg-[#2B5741]' : 'bg-gray-200'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}

export default function PriceCalculator() {
  const [type, setType] = useState<ProjectType>('fence');

  // Deck state
  const [deckWidth, setDeckWidth] = useState(12);
  const [deckLength, setDeckLength] = useState(16);
  const [deckMaterial, setDeckMaterial] = useState('cedar');
  const [hasRailing, setHasRailing] = useState(true);
  const [hasStairs, setHasStairs] = useState(false);
  const [hasPergola, setHasPergola] = useState(false);
  const [hasSeating, setHasSeating] = useState(false);

  // Fence state
  const [fenceLength, setFenceLength] = useState(80);
  const [fenceMaterial, setFenceMaterial] = useState('cedar');
  const [fenceHeight, setFenceHeight] = useState('6');
  const [fenceStyle, setFenceStyle] = useState('traditional');
  const [gateCount, setGateCount] = useState(1);

  const isWoodFence = WOOD_MATERIALS.includes(fenceMaterial);

  const estimate = useMemo(() => {
    let base = 0;
    if (type === 'deck') {
      const mat = DECK_MATERIALS.find((m) => m.id === deckMaterial)!;
      const sqft = deckWidth * deckLength;
      base = sqft * mat.rate;
      if (hasRailing) base += 2 * (deckWidth + deckLength) * 75;
      if (hasStairs) base += 900;
      if (hasPergola) base += 4500;
      if (hasSeating) base += 2000;
    } else {
      const mat = FENCE_MATERIALS.find((m) => m.id === fenceMaterial)!;
      const ht = FENCE_HEIGHTS.find((h) => h.id === fenceHeight)!;
      const style = FENCE_STYLES.find((s) => s.id === fenceStyle);
      let ratePerFt: number;
      if (fenceMaterial === 'pt' && isWoodFence) {
        ratePerFt = style?.ptRate ?? mat.rate;
      } else {
        const styleMultiplier = isWoodFence ? (style?.multiplier ?? 1.0) : 1.0;
        ratePerFt = mat.rate * styleMultiplier;
      }
      base = fenceLength * ratePerFt * ht.multiplier;
      base += gateCount * 380;
    }
    return { low: Math.round(base * 0.9), high: Math.round(base * 1.15), mid: Math.round(base) };
  }, [
    type, deckWidth, deckLength, deckMaterial, hasRailing, hasStairs, hasPergola, hasSeating,
    fenceLength, fenceMaterial, fenceHeight, fenceStyle, isWoodFence, gateCount,
  ]);

  return (
    <section id="calculator" className="bg-[#F7F5F1] py-20 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">INSTANT ESTIMATE</p>
          <h2
            className="text-[#2B5741] text-4xl font-bold mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Price Calculator
          </h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Get a ballpark estimate for your deck or fence project in seconds. Prices reflect Burlington-area market rates for 2026.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {/* Type tabs */}
          <div className="flex border-b border-gray-100">
            {(['fence', 'deck'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`flex-1 py-4 text-sm font-bold uppercase tracking-widest transition-colors cursor-pointer ${
                  type === t
                    ? 'bg-[#2B5741] text-white'
                    : 'bg-white text-gray-500 hover:text-[#2B5741]'
                }`}
              >
                {t === 'deck' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-home-4-line"></i> Deck
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-separator"></i> Fence
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="p-8">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* ── Left: Inputs ── */}
              <div className="flex-1 space-y-8">
                {type === 'deck' ? (
                  <>
                    {/* Size */}
                    <div>
                      <p className="text-xs font-bold text-[#2B5741] uppercase tracking-widest mb-4">Deck Size</p>
                      <div className="grid grid-cols-2 gap-4">
                        <InputNumber label="Width" value={deckWidth} onChange={setDeckWidth} min={4} max={60} unit="ft" />
                        <InputNumber label="Length" value={deckLength} onChange={setDeckLength} min={4} max={100} unit="ft" />
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Total area: <strong className="text-gray-600">{deckWidth * deckLength} sq ft</strong>
                      </p>
                    </div>

                    {/* Material */}
                    <div>
                      <p className="text-xs font-bold text-[#2B5741] uppercase tracking-widest mb-4">Decking Material</p>
                      <div className="grid grid-cols-2 gap-3">
                        {DECK_MATERIALS.map((m) => (
                          <MaterialCard
                            key={m.id}
                            item={m}
                            selected={deckMaterial === m.id}
                            onSelect={() => setDeckMaterial(m.id)}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Add-ons */}
                    <div>
                      <p className="text-xs font-bold text-[#2B5741] uppercase tracking-widest mb-4">Add-ons</p>
                      <div className="space-y-3.5">
                        <Toggle checked={hasRailing} onChange={() => setHasRailing(!hasRailing)} label="Railing" />
                        <Toggle checked={hasStairs} onChange={() => setHasStairs(!hasStairs)} label="Stairs (1 set)" />
                        <Toggle checked={hasPergola} onChange={() => setHasPergola(!hasPergola)} label="Pergola / Shade Structure" />
                        <Toggle checked={hasSeating} onChange={() => setHasSeating(!hasSeating)} label="Built-in Seating" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Fence length */}
                    <div>
                      <p className="text-xs font-bold text-[#2B5741] uppercase tracking-widest mb-4">Fence Length</p>
                      <InputNumber label="Total Length" value={fenceLength} onChange={setFenceLength} min={10} max={500} unit="ft" />
                    </div>

                    {/* Height */}
                    <div>
                      <p className="text-xs font-bold text-[#2B5741] uppercase tracking-widest mb-4">Fence Height</p>
                      <div className="flex gap-3">
                        {FENCE_HEIGHTS.map((h) => (
                          <button
                            key={h.id}
                            type="button"
                            onClick={() => setFenceHeight(h.id)}
                            className={`flex-1 py-3 rounded-lg border-2 text-sm font-bold transition-all cursor-pointer ${
                              fenceHeight === h.id
                                ? 'border-[#2B5741] bg-[#2B5741]/5 text-[#2B5741]'
                                : 'border-gray-200 text-gray-600 hover:border-[#2B5741]/40'
                            }`}
                          >
                            {h.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Material */}
                    <div>
                      <p className="text-xs font-bold text-[#2B5741] uppercase tracking-widest mb-4">Fence Material</p>
                      <div className="grid grid-cols-2 gap-3">
                        {FENCE_MATERIALS.map((m) => (
                          <MaterialCard
                            key={m.id}
                            item={m}
                            selected={fenceMaterial === m.id}
                            onSelect={() => {
                              setFenceMaterial(m.id);
                              if (!WOOD_MATERIALS.includes(m.id)) setFenceStyle('traditional');
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Fence Style — only for wood materials */}
                    {isWoodFence && (
                      <div>
                        <p className="text-xs font-bold text-[#2B5741] uppercase tracking-widest mb-1">Fence Style</p>
                        <p className="text-[11px] text-gray-400 mb-4">Choose the level of privacy for your wood fence</p>
                        <div className="flex flex-col gap-3">
                          {FENCE_STYLES.map((s) => (
                            <StyleCard
                              key={s.id}
                              item={s}
                              selected={fenceStyle === s.id}
                              onSelect={() => setFenceStyle(s.id)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Gates */}
                    <div>
                      <p className="text-xs font-bold text-[#2B5741] uppercase tracking-widest mb-4">Gates</p>
                      <InputNumber label="Number of Gates" value={gateCount} onChange={setGateCount} min={0} max={10} unit="gates" />
                    </div>
                  </>
                )}
              </div>

              {/* ── Right: Estimate panel ── */}
              <div className="lg:w-72 flex-shrink-0">
                <div className="sticky top-28 bg-[#2B5741] rounded-xl p-7 text-white">
                  <p className="text-[#C8A97E] text-[10px] font-bold uppercase tracking-[0.3em] mb-5">Your Estimate</p>

                  <div className="mb-6">
                    <p className="text-white/60 text-xs mb-1">Estimated Total</p>
                    <p
                      className="text-4xl font-bold leading-tight"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {formatCurrency(estimate.mid)}
                    </p>
                    <p className="text-white/50 text-xs mt-1.5">
                      Range: {formatCurrency(estimate.low)} – {formatCurrency(estimate.high)}
                    </p>
                  </div>

                  <div className="border-t border-white/20 pt-5 mb-6 space-y-2.5 text-sm">
                    {type === 'deck' ? (
                      <>
                        <div className="flex justify-between text-white/70">
                          <span>Area</span>
                          <span className="text-white font-medium">{deckWidth * deckLength} sq ft</span>
                        </div>
                        <div className="flex justify-between text-white/70">
                          <span>Material</span>
                          <span className="text-white font-medium capitalize">
                            {DECK_MATERIALS.find((m) => m.id === deckMaterial)?.label}
                          </span>
                        </div>
                        {hasRailing && (
                          <div className="flex justify-between text-white/70">
                            <span>Railing</span>
                            <span className="text-white font-medium">Included</span>
                          </div>
                        )}
                        {hasStairs && (
                          <div className="flex justify-between text-white/70">
                            <span>Stairs</span>
                            <span className="text-white font-medium">Included</span>
                          </div>
                        )}
                        {hasPergola && (
                          <div className="flex justify-between text-white/70">
                            <span>Pergola</span>
                            <span className="text-white font-medium">Included</span>
                          </div>
                        )}
                        {hasSeating && (
                          <div className="flex justify-between text-white/70">
                            <span>Built-in Seating</span>
                            <span className="text-white font-medium">Included</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between text-white/70">
                          <span>Length</span>
                          <span className="text-white font-medium">{fenceLength} ft</span>
                        </div>
                        <div className="flex justify-between text-white/70">
                          <span>Height</span>
                          <span className="text-white font-medium">
                            {FENCE_HEIGHTS.find((h) => h.id === fenceHeight)?.label}
                          </span>
                        </div>
                        <div className="flex justify-between text-white/70">
                          <span>Material</span>
                          <span className="text-white font-medium">
                            {FENCE_MATERIALS.find((m) => m.id === fenceMaterial)?.label}
                          </span>
                        </div>
                        {isWoodFence && (
                          <div className="flex justify-between text-white/70">
                            <span>Style</span>
                            <span className="text-white font-medium">
                              {FENCE_STYLES.find((s) => s.id === fenceStyle)?.label}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between text-white/70">
                          <span>Gates</span>
                          <span className="text-white font-medium">{gateCount}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <p className="text-white/50 text-[10px] leading-relaxed mb-5">
                    Estimates are approximate and based on Burlington-area 2026 material &amp; labour rates. Final pricing depends on site conditions, permits, and complexity.
                  </p>

                  <a
                    href="/#contact"
                    className="block w-full bg-[#C8A97E] text-white text-center py-3 text-xs font-bold uppercase tracking-[0.2em] rounded-md hover:bg-[#b8976a] transition-colors whitespace-nowrap"
                  >
                    GET AN EXACT QUOTE
                  </a>
                  <a
                    href="tel:9058694676"
                    className="flex items-center justify-center gap-2 mt-3 text-white/70 text-xs hover:text-white transition-colors"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-phone-fill text-xs"></i>
                    </div>
                    905-869-4676
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
